import { useCallback, useEffect, useMemo, useState } from 'react';

import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { GridFilterInputValueProps } from '@mui/x-data-grid-pro';

import { MasterIndexApi } from '@/api';
import { IMasterNamePerson } from '@/models';
import { getMasterNameDisplayName } from '@/utils';

type NameOption = {
  label: string;
  value: string;
};

const cachedOptionsById: Record<string, NameOption> = {};

const getSelectedIds = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string');
  }

  return [];
};

const mapPersonToOption = (person: IMasterNamePerson): NameOption | null => {
  const value = person._id ?? '';
  const label = getMasterNameDisplayName(person) || person.name || '';

  if (!value || !label) return null;

  return { label, value };
};

const getPeopleFilter = (inputValue: string) => {
  const quickFilterValues = inputValue
    .trim()
    .split(/[\s,]+/)
    .filter(Boolean);

  if (quickFilterValues.length === 0) return undefined;

  return JSON.stringify({
    items: [],
    quickFilterValues,
    quickFilterLogicOperator: 'and',
  });
};

const getSelectedPeopleFilter = (selectedIds: string[]) =>
  JSON.stringify({
    items: [
      {
        field: '_id',
        operator: 'isAnyOf',
        value: selectedIds,
      },
    ],
  });

export const CFSNamesFilterInput = (props: GridFilterInputValueProps) => {
  const selectedIds = useMemo(
    () => getSelectedIds(props.item.value),
    [props.item.value],
  );
  const [inputValue, setInputValue] = useState('');
  const [optionsById, setOptionsById] =
    useState<Record<string, NameOption>>(cachedOptionsById);
  const [searchOptions, setSearchOptions] = useState<NameOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const updateOptionsById = useCallback((options: NameOption[]) => {
    setOptionsById((previousOptionsById) => {
      const nextOptionsById = { ...previousOptionsById };

      options.forEach((option) => {
        cachedOptionsById[option.value] = option;
        nextOptionsById[option.value] = option;
      });

      return nextOptionsById;
    });
  }, []);

  useEffect(() => {
    let isMounted = true;

    if (!inputValue.trim() && !open) {
      setSearchOptions([]);
      return () => {
        isMounted = false;
      };
    }

    const timeoutId = window.setTimeout(() => {
      setLoading(true);

      MasterIndexApi.getMasterPersons({
        page: 1,
        limit: 50,
        sort: JSON.stringify({ createdAt: -1 }),
        filter: getPeopleFilter(inputValue),
      })
        .then((response) => {
          if (!isMounted) return;

          const nextSearchOptions = response.data.docs.flatMap((person) => {
            const option = mapPersonToOption(person);
            return option ? [option] : [];
          });

          setSearchOptions(nextSearchOptions);
          updateOptionsById(nextSearchOptions);
        })
        .finally(() => {
          if (isMounted) setLoading(false);
        });
    }, 300);

    return () => {
      isMounted = false;
      window.clearTimeout(timeoutId);
    };
  }, [inputValue, open, updateOptionsById]);

  useEffect(() => {
    let isMounted = true;

    if (selectedIds.length === 0) {
      return () => {
        isMounted = false;
      };
    }

    setSelectedLoading(true);

    MasterIndexApi.getMasterPersons({
      page: 1,
      limit: selectedIds.length,
      filter: getSelectedPeopleFilter(selectedIds),
    })
      .then((response) => {
        if (!isMounted) return;

        updateOptionsById(
          response.data.docs.flatMap((person) => {
            const option = mapPersonToOption(person);
            return option ? [option] : [];
          }),
        );
      })
      .finally(() => {
        if (isMounted) setSelectedLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [selectedIds, updateOptionsById]);

  const options = searchOptions.filter(
    (option) => !selectedIds.includes(option.value),
  );
  const value = selectedIds.flatMap((selectedId) => {
    const option = optionsById[selectedId] ?? cachedOptionsById[selectedId];
    return option ? [option] : [];
  });

  return (
    <Autocomplete
      multiple
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      loading={loading || selectedLoading}
      options={options}
      value={value}
      inputValue={inputValue}
      isOptionEqualToValue={(option, selected) =>
        option.value === selected.value
      }
      getOptionLabel={(option) => option.label}
      onInputChange={(_, nextInputValue, reason) => {
        if (reason === 'input' || reason === 'clear') {
          setInputValue(nextInputValue);
        }
      }}
      onChange={(_, nextValue) => {
        updateOptionsById(nextValue);
        props.applyValue({
          ...props.item,
          value: nextValue.map((option) => option.value),
        });
        setInputValue('');
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Value"
          placeholder="Filter value"
          variant="standard"
          InputLabelProps={{ shrink: true }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading || selectedLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      sx={{ minWidth: 240 }}
    />
  );
};
